import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Search, ChevronDown, ChevronUp, ScrollText } from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Input,
  Button,
  SegmentedControl,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { DataTable, StatusPill, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  useAesLogs,
  AES_EVENT_FILTERS,
  eventToneFor,
  isClientCanceled,
  clientCanceledWhere,
  streamOf,
  type AesLogEvent,
  type AesLogSource,
} from '@/api/aes';
import { useDebounce } from '../shared';
import { RefreshControl } from '../_shared/AnalyticsKit';
import { AesLogDetail } from './AesLogDetail';
import styles from './Aes.module.css';

const ALL = '__all__';
/** Rows shown before the user expands the tail. */
const COLLAPSED_ROWS = 8;

/**
 * Live-tail viewer over GET /logz. Filter by event prefix (e.g. the
 * upstream_error events behind a 502 spike) and a free-text substring (a stream
 * like `vtv6plus`, an IP, a token fingerprint). Click a row to inspect the raw
 * structured event in the right rail.
 */
export function AesLogViewer({ serviceId }: { serviceId?: string }) {
  const [source, setSource] = useState<AesLogSource>('logz');
  const [event, setEvent] = useState<string>(ALL);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<AesLogEvent | null>(null);
  const [refreshMs, setRefreshMs] = useState(0);

  // The text filter hits the control plane (q is in the query key), so debounce
  // it to avoid a request per keystroke.
  const debouncedQ = useDebounce(q.trim(), 350);

  // Polling is driven at the query level (refetchInterval) from the shared
  // RefreshControl's interval picker, so it stays tied to the current filter
  // (source/event/q): changing a filter restarts the poll on the new query key.
  const { data, isLoading, isError, error, refetch } = useAesLogs(serviceId, {
    source,
    event: event === ALL ? undefined : event,
    q: debouncedQ || undefined,
    limit: 300,
    refreshMs,
  });

  const columns = useMemo<ColumnDef<AesLogEvent, unknown>[]>(
    () => [
      {
        accessorKey: 'ts',
        header: 'Time',
        size: 96,
        cell: ({ row }) => (
          <span className={styles.cellMuted}>{dayjs(row.original.ts).format('HH:mm:ss')}</span>
        ),
      },
      {
        accessorKey: 'event',
        header: 'Event',
        size: 210,
        cell: ({ row }) => (
          <StatusPill tone={eventToneFor(row.original)}>
            <span className={styles.eventName}>{row.original.event.replace(/^live\./, '')}</span>
          </StatusPill>
        ),
      },
      {
        id: 'stream',
        header: 'Stream',
        size: 96,
        cell: ({ row }) => streamOf(row.original) ?? <span className={styles.cellMuted}>—</span>,
      },
      {
        accessorKey: 'ip',
        header: 'Client IP',
        size: 120,
        cell: ({ row }) => row.original.ip ?? <span className={styles.cellMuted}>—</span>,
      },
      {
        id: 'detail',
        header: 'Detail',
        cell: ({ row }) => {
          const ev = row.original;
          if (isClientCanceled(ev)) {
            return (
              <span className={styles.cellMuted}>client canceled · {clientCanceledWhere(ev)} (499)</span>
            );
          }
          const text = ev.reason ?? (ev.status != null ? `status ${ev.status}` : ev.method) ?? '';
          return <span className={styles.cellMuted}>{String(text)}</span>;
        },
      },
    ],
    [],
  );

  const events = data?.events ?? [];
  // Keep the tail compact by default; let the user expand to the full buffer.
  const visible = expanded ? events : events.slice(0, COLLAPSED_ROWS);
  const hiddenCount = events.length - visible.length;

  // 404 means the /logz route (or its proxy mapping) is missing — a distinct
  // failure from auth/reachability, so surface it with its own hint.
  const status = (error as { response?: { status?: number } })?.response?.status;
  const errorDescription =
    status === 404
      ? 'Log endpoint not found (404) — the gateway /logz route or its proxy mapping is missing.'
      : ((error as { message?: string })?.message ??
        'Check APP_AES_API_URL / admin key, or that the gateway is reachable.');

  // The table fills the width on its own; the detail rail only appears once a row
  // is selected, so the live tail isn't permanently squeezed beside an empty rail.
  const table = (
    <>
      <DataTable
        columns={columns}
        data={visible}
        getRowId={(r, i) => `${r.ts}-${i}`}
        dense
        onRowClick={(r) => setSelected(r)}
        emptyState={
          <EmptyState
            icon={<ScrollText size={20} />}
            title="No events"
            description="No buffered events match the current filter."
          />
        }
      />
      {(hiddenCount > 0 || expanded) && events.length > COLLAPSED_ROWS && (
        <div className={styles.expandRow}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            startIcon={expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          >
            {expanded ? 'Show fewer' : `Show all ${events.length} events`}
          </Button>
        </div>
      )}
      {data && (
        <div className={styles.meta}>
          <span>
            Showing {visible.length} of {data.count} matched · {data.total} ever seen
          </span>
          <span>Ring capacity {data.capacity}</span>
        </div>
      )}
      <p className={styles.note}>
        {source === 'errorz'
          ? `Server errors only — every 5xx, from Loki. `
          : source === 'rejectz'
            ? `Client errors (4xx) — auth rejects + forwarded upstream 4xx, from Loki. `
            : `Audit events from Loki, newest first. `}
        Showing up to {data?.capacity ?? '—'} in the window
        {data?.window ? ` (last ${data.window})` : ''}.{' '}
        For longer ranges or full history, query Loki / Grafana directly.
      </p>
    </>
  );

  return (
    <>
      <Toolbar
        start={
          <>
            {/* Source picker (all from Loki): all events / 5xx server errors /
                4xx client errors. Errors and rejects are split so neither buries
                the other in the result window. */}
            <SegmentedControl
              aria-label="Log source"
              value={source}
              onValueChange={(v) => setSource(v as AesLogSource)}
              items={[
                { value: 'logz', label: 'All' },
                { value: 'errorz', label: 'Server errors' },
                { value: 'rejectz', label: 'Rejects (4xx)' },
              ]}
            />
            <Select value={event} onValueChange={(v) => setEvent(v)}>
              <SelectTrigger style={{ minWidth: 'min(220px, 100%)' }}>
                <SelectValue placeholder="Event" />
              </SelectTrigger>
              <SelectContent>
                {AES_EVENT_FILTERS.map((f) => (
                  <SelectItem key={f.value || ALL} value={f.value || ALL}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className={styles.search}
              startSlot={<Search />}
              placeholder="Filter logs (stream, IP, token…)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </>
        }
        end={<RefreshControl onRefresh={() => refetch()} onIntervalChange={setRefreshMs} />}
      />

      {isError ? (
        <EmptyState title="Could not load logs" description={errorDescription} />
      ) : isLoading ? (
        <Skeleton variant="rect" height={240} />
      ) : (
        table
      )}

      {/* Row click opens the raw event in a dialog (same as the WAF event log). */}
      <AesLogDetail event={selected} onClose={() => setSelected(null)} />
    </>
  );
}

export default AesLogViewer;
